import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

// ===================== LEAVE CONFIG =====================

export const fetchLeaveConfigs = createAsyncThunk(
    'hrms/fetchLeaveConfigs',
    async (accountId: string): Promise<any> => {
        const response = await axios.get(`/api/v1/leaves/config/${accountId}`);
        return response.data;
    }
);

export const createLeaveConfig = createAsyncThunk(
    'hrms/createLeaveConfig',
    async ({ accountId, data }: { accountId: string; data: any }): Promise<any> => {
        const response = await axios.post(`/api/v1/leaves/config/${accountId}`, data);
        return response.data;
    }
);

export const updateLeaveConfig = createAsyncThunk(
    'hrms/updateLeaveConfig',
    async ({ configId, data }: { configId: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/leaves/config/${configId}`, data);
        return response.data;
    }
);

export const deleteLeaveConfig = createAsyncThunk(
    'hrms/deleteLeaveConfig',
    async (configId: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/leaves/config/${configId}`);
        return response.data;
    }
);

// ===================== SHIFT PATTERN CONFIG =====================

export const fetchShiftConfigs = createAsyncThunk(
    'hrms/fetchShiftConfigs',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/shiftPatternConfig`);
        return response.data;
    }
);

export const createShiftConfig = createAsyncThunk(
    'hrms/createShiftConfig',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/shiftPatternConfig`, data);
        return response.data;
    }
);

export const updateShiftConfig = createAsyncThunk(
    'hrms/updateShiftConfig',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/shiftPatternConfig/${id}`, data);
        return response.data;
    }
);

export const deleteShiftConfig = createAsyncThunk(
    'hrms/deleteShiftConfig',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/shiftPatternConfig/${id}`);
        return response.data;
    }
);

// ===================== SHIFT PATTERN CONFIG (additional) =====================

export const assignShiftPatternToEmployee = createAsyncThunk(
    'hrms/assignShiftPatternToEmployee',
    async ({ employeeId, configId }: { employeeId: string; configId: string }): Promise<any> => {
        const response = await axios.post(`/api/v1/employee/${employeeId}/shift-pattern-config/${configId}`);
        return response.data;
    }
);

export const assignWeekOffToEmployee = createAsyncThunk(
    'hrms/assignWeekOffToEmployee',
    async ({ employeeId, weekOffId }: { employeeId: string; weekOffId: string }): Promise<any> => {
        const response = await axios.post(`/api/v1/employee/${employeeId}/week-off/${weekOffId}`);
        return response.data;
    }
);

export const fetchEmployeesByAccount = createAsyncThunk(
    'hrms/fetchEmployeesByAccount',
    async ({ accountId, page = 1, limit = 10, filter = '' }: { accountId: string; page?: number; limit?: number; filter?: string }): Promise<any> => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (filter) params.append('filter', filter);
        const response = await axios.get(`/api/v1/employee/account/${accountId}/emp-list?${params}`);
        return response.data;
    }
);

// ===================== HOLIDAYS =====================

export const fetchHolidays = createAsyncThunk(
    'hrms/fetchHolidays',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/holidays?isActive=true`);
        return response.data;
    }
);

export const createHoliday = createAsyncThunk(
    'hrms/createHoliday',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/holidays`, data);
        return response.data;
    }
);

export const updateHoliday = createAsyncThunk(
    'hrms/updateHoliday',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/holidays/${id}`, data);
        return response.data;
    }
);

export const deleteHoliday = createAsyncThunk(
    'hrms/deleteHoliday',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/holidays/${id}`);
        return response.data;
    }
);

export const importHolidays = createAsyncThunk(
    'hrms/importHolidays',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/holidays/import`, data);
        return response.data;
    }
);

// ===================== SHIFTS =====================

export const fetchShifts = createAsyncThunk(
    'hrms/fetchShifts',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/worktime/shifts`);
        return response.data;
    }
);

export const createShift = createAsyncThunk(
    'hrms/createShift',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/worktime/shifts`, data);
        return response.data;
    }
);

export const updateShift = createAsyncThunk(
    'hrms/updateShift',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/worktime/shifts/${id}`, data);
        return response.data;
    }
);

export const deleteShift = createAsyncThunk(
    'hrms/deleteShift',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/worktime/shifts/${id}`);
        return response.data;
    }
);

// ===================== WEEK OFFS =====================

export const fetchWeekOffs = createAsyncThunk(
    'hrms/fetchWeekOffs',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/worktime/week-offs`);
        return response.data;
    }
);

export const createWeekOff = createAsyncThunk(
    'hrms/createWeekOff',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/worktime/week-offs`, data);
        return response.data;
    }
);

export const updateWeekOff = createAsyncThunk(
    'hrms/updateWeekOff',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/worktime/week-offs/${id}`, data);
        return response.data;
    }
);

export const deleteWeekOff = createAsyncThunk(
    'hrms/deleteWeekOff',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/worktime/week-offs/${id}`);
        return response.data;
    }
);
